import { type breakPointsOptions, type BreakPoints } from './types'
import type { TokenPath } from '../tokens/generated/types'
export type * from './types'

/** Storybook demo labels — matches `StyledBreakpoints` background tokens per tier. */
export const breakpointToToken: Record<breakPointsOptions, TokenPath> = {
  xs: 'background.main.pressed',
  sm: 'background.main.pressed',
  md: 'background.tonal.soft.default',
  lg: 'foreground.main.disabled',
  xl: 'foreground.main.secondary'
}

export const breakpoints: BreakPoints = {
  values: {
    xs: 0,
    sm: 905,
    md: 1280,
    lg: 1440,
    xl: 1648
  }
}

// =============================================================================
// DASHBOARD-ONLY BREAKPOINTS
// These constants are ONLY for use within the dashboards framework
// (ui/apps/me/shared/dashboards/). Do not use them elsewhere in the app —
// use the Kurama theme breakpoints above for everything else.
// =============================================================================

/**
 * Dashboard grid column count per breakpoint.
 * X-small uses 8 columns; all larger sizes use 12.
 */
export const DASHBOARD_GRID_COLUMNS = {
  xs: 8, // X-small (764–1003px): 8 columns
  sm: 12 // Small+ (1004px+): 12 columns
} as const

/**
 * Dashboard pixel breakpoints.
 * Derived from the design system spec:
 *   X-small: 764–1003px  (8 col, 16px gutter)
 *   Small:   1004–1279px (12 col, 16px gutter)
 *   Medium:  1280–1539px (12 col, 24px gutter)
 *   Large:   1540–2499px (12 col, 24px gutter)
 *   X-large: 2500px+     (12 col, 24px gutter)
 */
export const DASHBOARD_BREAKPOINTS = {
  xsmall: 764,
  small: 1004,
  medium: 1280,
  large: 1540,
  xlarge: 2500
} as const

/**
 * Dashboard media query strings.
 * Use in dashboard styled components instead of theme.breakpoints.
 *
 * @example
 * ```tsx
 * const MyComponent = styled('div')({
 *   padding: '16px',
 *   [`@media ${DASHBOARD_MEDIA_QUERIES.medium}`]: { padding: '24px' }
 * })
 * ```
 */
export const DASHBOARD_MEDIA_QUERIES = {
  xsmall: `(min-width: ${DASHBOARD_BREAKPOINTS.xsmall}px)`,
  small: `(min-width: ${DASHBOARD_BREAKPOINTS.small}px)`,
  medium: `(min-width: ${DASHBOARD_BREAKPOINTS.medium}px)`,
  large: `(min-width: ${DASHBOARD_BREAKPOINTS.large}px)`,
  xlarge: `(min-width: ${DASHBOARD_BREAKPOINTS.xlarge}px)`,
  belowSmall: `(max-width: ${DASHBOARD_BREAKPOINTS.small - 1}px)`
} as const

/**
 * Dashboard gutter sizes in pixels.
 *   16px — X-small & Small breakpoints
 *   24px — Medium and above
 */
export const DASHBOARD_GRID_SPACING = {
  small: 16,
  large: 24
} as const
